import { gql, useQuery } from "@apollo/client"
import { SUGGESTED_BENEFITS_PROVISIONED_ENROLLMENT_FRAGMENT } from "../../pages/TabBenefits/gql/fragments"
import { ENROLLMENT_COST_CONTRIBUTIONS_FRAGMENT } from "../../fragments/EnrollmentCostContributionsFragment"
import { MONEY_FIELDS } from "~/shared/fragments/MoneyFields"
import { InjectedOptions } from "@oysterhr/core"
import { Engagement } from "@oysterhr/schema"

export const TEAM_MEMBER_ENROLLMENTS_QUERY_NAME = "BenefitsStaffTeamMemberPackages"

const BENEFITS_ENROLLMENT_FIELDS_FRAGMENT = gql`
  ${MONEY_FIELDS}
  ${ENROLLMENT_COST_CONTRIBUTIONS_FRAGMENT}
  fragment BenefitsEnrollmentFragment on BenefitsEnrollment {
    id
    state
    coverageStartDate
    cancelledAt
    enrolledAt
    coverageEndDate
    availableEvents
    cancellationReason
    employerContribution
    ...EnrollmentCostContributionsFragment
    totalPrice {
      ...MoneyFields
    }
    dependents {
      collection {
        id
      }
    }
    engagement {
      id
      dependents {
        collection {
          id
          fieldsWithValues
        }
        metadata {
          totalCount
        }
      }
    }
    dependentEnrollments {
      collection {
        id
      }
    }
    currentPrice {
      effectiveDate
      employerContribution {
        ...MoneyFields
      }
    }
    totalPriceInvoicedCompany {
      ...MoneyFields
    }
    pricePaidProvider {
      ...MoneyFields
    }

    tier {
      id
      name
      fee(engagementId: $engagementId, useSalaryCurrencyCode: $useSalaryCurrencyCode) {
        subunit
        formatted
        currencyCode
      }
    }
    plan {
      id
      name
      category
      guideUrl
      descriptiveName
      costSplitAllowed
      provider {
        id
        name
        automated
      }
      priceEstimate {
        currencies {
          currencyCode
        }
      }
      fee(engagementId: $engagementId, useSalaryCurrencyCode: $useSalaryCurrencyCode) {
        subunit
        formatted
        currencyCode
      }
    }
  }
`

export const TEAM_MEMBER_ENROLLMENTS = gql`
  ${SUGGESTED_BENEFITS_PROVISIONED_ENROLLMENT_FRAGMENT}
  ${BENEFITS_ENROLLMENT_FIELDS_FRAGMENT}
  query ${TEAM_MEMBER_ENROLLMENTS_QUERY_NAME}(
    $engagementId: ID!
    $currencyCode: String!
    $engagementType: String!
    $useSalaryCurrencyCode: Boolean
  ) {
    engagement(engagementId: $engagementId) {
      id
      payrollCurrencyCode
      ...SuggestedBenefitsProvisionedEnrollmentFragment
      benefitsEnrollments {
        collection {
          ...BenefitsEnrollmentFragment
        }
      }
      benefitsEnrolledPackages {
        collection {
          id
          createdAt
          tier {
            id
            name
            fee(engagementId: $engagementId, useSalaryCurrencyCode: $useSalaryCurrencyCode) {
              subunit
              formatted
              currencyCode
            }
          }
          enrollments {
            collection {
                ...BenefitsEnrollmentFragment
            }
          }
        }
      }
    }
  }
`

interface TeamMemberBenefitPackagesPayload {
  engagement: Engagement
}

interface TeamMemberBenefitPackagesInput {
  engagementId: string
  currencyCode: string
  engagementType: string
  useSalaryCurrencyCode?: boolean
}

export const useTeamMemberBenefitPackages = (
  options: InjectedOptions<TeamMemberBenefitPackagesPayload, TeamMemberBenefitPackagesInput>,
) => useQuery(TEAM_MEMBER_ENROLLMENTS, { ...options, fetchPolicy: "no-cache" })
